export interface ApiEnvelopeMeta {
  request_id: string;
  timestamp: string;
  /**
   * Legacy fields kept for tolerance against older internal endpoints that
   * still emit `meta.next_cursor` or `meta.pagination.*`. Public list
   * endpoints have moved to the canonical paged payload at `envelope.data`
   * (see `@fraudefender/api-envelope`'s `paged()`); consumers should read
   * pagination off `data` going forward, not `meta`.
   */
  next_cursor?: string | null;
  /**
   * camelCase id-cursor still emitted by a few internal list endpoints (e.g.
   * `_reseller/merchants`). Numeric (last row id) or string; null on last page.
   * Read via {@link serverFetchWithMeta} for "Load more" pagination.
   */
  nextCursor?: string | number | null;
  pagination?: unknown;
}

export interface ApiEnvelopeError {
  code: string;
  message: string;
  details?: unknown;
}

export type ApiEnvelope<T> =
  | { success: true; data: T; error: null; meta: ApiEnvelopeMeta }
  | { success: false; data: null; error: ApiEnvelopeError; meta: ApiEnvelopeMeta };

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  isUnauthenticated(): boolean {
    return this.status === 401;
  }
  isForbidden(): boolean {
    return this.status === 403;
  }
  isNotFound(): boolean {
    return this.status === 404;
  }
  isConflict(): boolean {
    return this.status === 409;
  }
}

function isMalformed(env: unknown): env is never {
  if (env === null || typeof env !== 'object') return true;
  const e = env as Record<string, unknown>;
  if (typeof e.success !== 'boolean') return true;
  if (e.success === true && !('data' in e)) return true;
  if (e.success === false && (e.error === null || typeof e.error !== 'object')) return true;
  return false;
}

/**
 * Validates the envelope and returns `data` from a successful response.
 * Throws `ApiError` on failure envelopes or malformed bodies. Pagination
 * fields live inside `data` on list endpoints (see `paged()` in
 * `@fraudefender/api-envelope`); callers needing pagination just narrow the
 * generic to the canonical paged-payload shape.
 */
export function parseEnvelope<T>(env: ApiEnvelope<T> | unknown, status: number): T {
  if (isMalformed(env)) {
    throw new ApiError('malformed_envelope', 'malformed API response envelope', status);
  }
  const e = env as ApiEnvelope<T>;
  if (e.success) return e.data;
  throw new ApiError(e.error.code, e.error.message, status, e.error.details);
}
